import React, { useCallback, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Icon,
  IconButton,
  Link,
  Switch,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import Uppy from '@uppy/core';
import { useUppy } from '@uppy/react';
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheck,
  FaCloudDownloadAlt,
  FaDownload,
  FaInfoCircle,
  FaPen,
  FaTrash,
  FaVideo,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { format as formatDate, isValid as isValidDate } from 'date-fns';
import { dateFnsOptions } from '../../../_utils/dateFnsLocale';
import { getCurrentLocaleCode, getLocale } from '../../../_utils/uppyLocale';
import { parseToArray } from '../../../_utils/csv';
import SocialAccountPropType from '../../../_types/SocialAccount';
import UppyDropFile from '../UppyDropFile/UppyDropFile';
import * as ImportPostStatus from './ImportPostStatus.constants';
import PostsBulkImportItem from './PostsBulkImportItem';
import {
  addBufferPost,
  addSchedulePost,
  refreshPostsView,
  setPostsBulkImportItems,
  updatePostsBulkImportItem,
  removePostsBulkImportItem,
  getAvailablePostsQuota,
} from '../../../_actions/plannerActions';
import { validUrl } from '../../../_utils/validator';
import { getUrlWithProtocol } from '../PostEditor/utils';
import useScraper from '../PostLinkPreview/useScraper';
import {
  getAccountsForTab,
  getSeparatedData,
  getStateForTab,
  getTabsForAccounts,
} from "../PostEditor/helpers";
import PostPreviewModal from "../PostPreview/PostPreviewModal";
import PostEditorViewModal from "../PostEditorView/PostEditorViewModal";
import * as PostEditorModes from "../PostEditor/PostEditorModes.constants";
import * as PostEditorActions from "../PostEditor/PostEditorActions.constants";
import { getErrors } from "../PostEditor/restrictions";
import { getFeatureLimit, isFeatureAvailable } from "../../../_utils/common";
import { FeatureCodes } from "../../details/constants";
import TldrUpgradeSubscriptionModal from "../../common/modals/TldrUpgradeSubscriptionModal";
import SocialAccountsDropdown from "../SocialAccountsDropdown/SocialAccountsDropdown";
import Alert from "../Alert/Alert";
import useUserSettings from "../../../_hooks/useUserSettings";
import { showToast } from "../../../_utils/showToast";

const generateGroupId = (workspaceId, userId) =>
  `bi-${workspaceId}-${userId}-${new Date().getTime()}`;

const exportPostData = (accounts, post, groupId) => {
  const data = {};
  const tabs = getTabsForAccounts(accounts, post.accounts);

  tabs.forEach((tab) => {
    const { id, ...postData } = getStateForTab(getSeparatedData(post), tab);
    const providerAccounts = getAccountsForTab(accounts, post.accounts, tab);
    const providerAccountsIds = providerAccounts.map((item) => item.id);

    data[tab] = {
      accounts: providerAccountsIds,
      date: post.date,
      time: post.time,
      timezone: post.timezone,
      ...postData,
      groupId,
    };
  });

  return data;
};

const PostsBulkImport = ({
  defaultAccountId,
  accounts,
  onClose,
  onProgress,
}) => {
  const { t } = useTranslation(["planner", "common"]);
  const [userSettings] = useUserSettings();
  const [isUploading, setIsUploading] = useState(false);
  const [importInProgress, setImportInProgress] = useState(false);
  const [importEnd, setImportEnd] = useState(false);
  const [automaticTime, setAutomaticTime] = useState(false);
  const [showAllItems, setShowAllItems] = useState(false);
  const [preparingInProgress, setPreparingInProgress] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [items, setItems] = useState([]);
  const [fileName, setFileName] = useState('');
  const [fileData, setFileData] = useState({});
  const [accountId, setAccountId] = useState(
    defaultAccountId || accounts?.[0]?.id
  );
  const [itemEdit, setItemEdit] = useState(null);
  const [itemPreview, setItemPreview] = useState(null);
  const [itemsLimit, setItemsLimit] = useState(0);
  const [workspaceFeatures, setWorkspaceFeatures] = useState({
    bulkImport: false,
    canAddSchedule: false,
    canAddBuffer: false,
  });
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const { fetchScraperData } = useScraper();
  const {
    isOpen: showUpgradeModal,
    onOpen: handleOpenUpgradeModal,
    onClose: handleCloseUpgradeModal,
  } = useDisclosure();
  const dispatch = useDispatch();
  const postsBulkImportItems = useSelector(
    (state) => state.plannerStore.postsBulkImportItems
  );
  const userId = useSelector((state) => state.auth.payload?.user?.pk);
  const subscriptionData = useSelector((state) => state.subscription);
  const workspace = useSelector((state) =>
    state.auth.payload?.orgs?.find(
      (item) => item.id === state.auth.payload?.selectedOrg
    )
  );
  const defaultTimezone = useMemo(
    () => workspace?.settings?.timezone,
    [workspace]
  );
  const itemsWithValidationError = useMemo(
    () =>
      postsBulkImportItems.filter(
        (item) => item.status === ImportPostStatus.VALIDATION_ERROR
      ).length,
    [postsBulkImportItems]
  );
  const itemsWithImportError = useMemo(
    () =>
      postsBulkImportItems.filter(
        (item) => item.status === ImportPostStatus.IMPORT_ERROR
      ).length,
    [postsBulkImportItems]
  );
  const itemsWithImportSuccess = useMemo(
    () =>
      postsBulkImportItems.filter(
        (item) => item.status === ImportPostStatus.IMPORT_SUCCESS
      ).length,
    [postsBulkImportItems]
  );
  const itemsWithError = useMemo(
    () =>
      postsBulkImportItems.filter((item) =>
        [
          ImportPostStatus.VALIDATION_ERROR,
          ImportPostStatus.IMPORT_ERROR,
        ].includes(item.status)
      ),
    [postsBulkImportItems]
  );
  const itemsToShow = useMemo(() => {
    if (itemsWithError.length > 0 && !showAllItems) {
      return itemsWithError;
    }

    return postsBulkImportItems;
  }, [itemsWithError, postsBulkImportItems, showAllItems]);

  const maxFileSize = 1024 * 1024 * 5;
  const uppy = useUppy(
    () =>
      new Uppy({
        id: 'posts-bulk-import',
        autoProceed: true,
        allowMultipleUploadBatches: false,
        restrictions: {
          maxFileSize,
          allowedFileTypes: ['.csv'],
          maxNumberOfFiles: 1,
        },
        locale: getLocale(getCurrentLocaleCode()),
      })
  );

  const handleAccountChange = useCallback(
    (account) => setAccountId(account.id),
    [setAccountId]
  );

  const handleAutomaticTimeToggle = useCallback(
    () => setAutomaticTime(!automaticTime),
    [setAutomaticTime, automaticTime]
  );

  const handleShowAllItemsToggle = useCallback(
    () => setShowAllItems(!showAllItems),
    [setShowAllItems, showAllItems]
  );

  const handleResetSelectedFile = useCallback(() => {
    setItems([]);
    setFileName('');
  }, [setItems, setFileName]);

  const handleNextStep = useCallback(async () => {
    if (!accountId) {
      showToast({
        description: t('posts_bulk_import.errors.no_accounts_selected'),
        title: 'Error',
        status: 'error',
      });

      return;
    }

    if (!items.length) {
      showToast({
        description: t('posts_bulk_import.errors.no_posts_selected'),
        title: 'Error',
        status: 'error',
      });

      return;
    }

    if (
      !workspaceFeatures.bulkImport ||
      (!automaticTime && !workspaceFeatures.canAddSchedule) ||
      (automaticTime && !workspaceFeatures.canAddBuffer)
    ) {
      handleOpenUpgradeModal();
      return;
    }

    const groupId = generateGroupId(workspace?.id, userId);

    setPreparingInProgress(true);
    setProgressPercent(0);
    setImportEnd(false);

    try {
      const postsQuotaResponse = await dispatch(getAvailablePostsQuota());

      if (
        postsQuotaResponse?.data?.availableQuota !== undefined &&
        postsQuotaResponse.data.availableQuota < items.length
      ) {
        showToast({
          description: t('posts_bulk_import.errors.posts_quota', {
            availableQuota: postsQuotaResponse.data.availableQuota,
            count: items.length,
          }),
          title: 'Error',
          status: 'error',
        });

        setPreparingInProgress(false);

        return;
      }
    } catch (error) {}

    const getDate = (item) => {
      if (!item.date || !item.time) {
        return null;
      }

      const date = new Date(`${item.date} ${item.time}`);

      return isValidDate(date) ? date : null;
    };

    const getScraper = async (item) => {
      if (!item.link || !validUrl(item.link)) {
        return null;
      }

      const link = getUrlWithProtocol(item.link);

      try {
        const data = await fetchScraperData(link);

        if (data) {
          return {
            link,
            title: data?.title || '',
            description: data?.description || '',
            image: data?.images?.[0] || '',
          };
        }
      } catch (e) {}

      return null;
    };

    const itemsLength = items.length;
    let currentItem = 0;

    const itemsData = await Promise.all(
      items.map(async (item, index) => {
        const date = automaticTime ? null : getDate(item);
        const scraper = await getScraper(item);
        const accountsIds = [accountId];
        const postData = {
          message: item.message,
          scraper,
        };
        const errors = [];

        if (!automaticTime) {
          if ((item.date || item.time) && !date) {
            errors.push({
              message: t('posts_bulk_import.errors.post_invalid_date_time', {
                date: item.date,
                time: item.time,
              }),
            });
          } else if (!item.date && !item.time) {
            errors.push({
              message: t('posts_bulk_import.errors.post_no_date_time'),
            });
          }
        }

        if (item.link && !scraper) {
          errors.push({
            message: t('posts_bulk_import.errors.post_invalid_link', {
              link: item.link,
            }),
          });
        }

        if (!item.message && !scraper) {
          errors.push({
            message: t('posts_bulk_import.errors.post_no_content'),
          });
        }

        const tab = getTabsForAccounts(accounts, accountsIds)[0];
        const { id, ...tabData } = getStateForTab(getSeparatedData(postData), tab);
        const providerAccounts = getAccountsForTab(accounts, accountsIds, tab);
        const providerAccountsIds = providerAccounts.map((item) => item.id);
        const tabErrors = (await getErrors(tab, "", providerAccountsIds, tabData, {
          connectedDevices: userSettings.connectedDevices,
        })).filter((item) => !item.warning);

        if (tabErrors.length) {
          const tabErrorsMessages = tabErrors.map((error) => ({
            message: t(`post_editor.errors.${error.id}`, error.values)
          }));
          errors.push(...tabErrorsMessages);
        }

        currentItem = currentItem + 1;
        setProgressPercent(Math.ceil((currentItem * 100) / itemsLength));

        return {
          id: `post-${index}`,
          groupId,
          status: errors.length
            ? ImportPostStatus.VALIDATION_ERROR
            : ImportPostStatus.READY_TO_IMPORT,
          errors,
          post: {
            accounts: accountsIds,
            date: date ? formatDate(date, "yyyy-MM-dd", dateFnsOptions()) : "",
            time: date ? formatDate(date, "HH:mm", dateFnsOptions()) : "",
            timezone: defaultTimezone,
            ...tabData,
          },
        };
      })
    );

    setPreparingInProgress(false);
    setProgressPercent(0);
    setShowAllItems(false);

    dispatch(setPostsBulkImportItems(itemsData));
  }, [
    dispatch,
    t,
    setPreparingInProgress,
    setProgressPercent,
    setShowAllItems,
    fetchScraperData,
    accounts,
    accountId,
    items,
    defaultTimezone,
    workspace?.id,
    userId,
    automaticTime,
    workspaceFeatures,
    userSettings?.connectedDevices,
    handleOpenUpgradeModal,
  ]);

  const handlePreviousStep = useCallback(() => {
    dispatch(setPostsBulkImportItems([]));
  }, [dispatch]);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleImport = useCallback(
    async (retry) => {
      setImportInProgress(true);
      setProgressPercent(0);

      const items = postsBulkImportItems.filter(
        (item) =>
          item.status === ImportPostStatus.READY_TO_IMPORT ||
          (retry && item.status === ImportPostStatus.IMPORT_ERROR)
      );
      const itemsLength = items.length;
      let currentItem = 0;
      let interruptImport = false;
      let interruptImportMessage = '';

      for (const item of items) {
        const data = exportPostData(accounts, item.post, item.groupId);

        dispatch(
          updatePostsBulkImportItem({
            ...item,
            status: ImportPostStatus.IMPORTING,
            errors: [],
          })
        );

        const onSuccess = () => {
          dispatch(
            updatePostsBulkImportItem({
              ...item,
              status: ImportPostStatus.IMPORT_SUCCESS,
              errors: [],
            })
          );
        };

        const onError = (error) => {
          dispatch(
            updatePostsBulkImportItem({
              ...item,
              status: ImportPostStatus.IMPORT_ERROR,
              errors: error ? [{ message: error }] : [],
            })
          );
        };

        if (interruptImport) {
          onError(interruptImportMessage);
        } else {
          try {
            const response = automaticTime
              ? await dispatch(addBufferPost(data, { hideToast: true }))
              : await dispatch(addSchedulePost(data, { hideToast: true }));

            if (response?.data?.status === 1) {
              onSuccess(response.data?.results?.[0]?.message);
            } else {
              onError(response.data?.results?.[0]?.message);
            }
          } catch (error) {
            const errorMessage =
              error?.response?.data?.results?.[0]?.message || error.message;

            onError(errorMessage);
            showToast({
              description: errorMessage,
              title: 'Error',
              status: 'error',
            });

            interruptImport = true;
            interruptImportMessage = errorMessage;
          }
        }

        currentItem = currentItem + 1;
        setProgressPercent(Math.ceil((currentItem * 100) / itemsLength));
      }

      setImportInProgress(false);
      setProgressPercent(0);
      setImportEnd(true);
      setShowAllItems(false);
    },
    [
      dispatch,
      setImportInProgress,
      setProgressPercent,
      setImportEnd,
      setShowAllItems,
      postsBulkImportItems,
      accounts,
      automaticTime,
    ]
  );

  const handleRetry = useCallback(() => {
    handleImport(true);
  }, [handleImport]);

  const handleDeleteItem = useCallback(
    (item) => {
      dispatch(removePostsBulkImportItem(item.id));
    },
    [dispatch]
  );

  const handleEditItem = useCallback(
    (item) => {
      setItemEdit(item);
    },
    [setItemEdit]
  );

  const handleActionEditItem = useCallback(
    (action, data) => {
      if (action !== PostEditorActions.SAVE) {
        return;
      }

      const tab = Object.keys(data)[0];

      dispatch(
        updatePostsBulkImportItem({
          ...itemEdit,
          status: ImportPostStatus.READY_TO_IMPORT,
          errors: [],
          post: {
            ...data[tab],
          },
        })
      );
    },
    [dispatch, itemEdit]
  );

  const handleCloseEditItem = useCallback(() => {
    setItemEdit(null);
  }, [setItemEdit]);

  const handlePreviewItem = useCallback(
    (item) => {
      const data = exportPostData(accounts, item.post);

      setItemPreview({
        accounts: accounts.filter((account) =>
          item.post.accounts.includes(account.id)
        ),
        postsByProvider: data,
      });
    },
    [setItemPreview, accounts]
  );

  const handleClosePreviewItem = useCallback(() => {
    setItemPreview(null);
  }, [setItemPreview]);

  useEffect(() => {
    const onUppyUpload = ({ fileIDs }) => {
      const file = uppy.getFile(fileIDs?.[0]);
      if (file) {
        setFileData(file);
        const name = file.name || '';

        setIsUploading(true);

        const onError = (error) => {
          setItems([]);
          setFileName('');
          showToast({
            description: error,
            title: 'Error',
            status: 'error',
          });
        };

        file.data
          .text()
          .then((stringData) => {
            try {
              const data = parseToArray(stringData);

              if (!itemsLimit || data.length <= itemsLimit) {
                const itemsData = data.map((item) => ({
                  date: item[0] || '',
                  time: item[1] || '',
                  message: item[2] || '',
                  link: item[3] || '',
                }));

                setItems(itemsData);
                setFileName(name);
              } else {
                onError(
                  t('posts_bulk_import.errors.row_limit_exceeded', {
                    count: itemsLimit,
                  })
                );
              }
            } catch (error) {
              onError(t('posts_bulk_import.errors.parse_file'));
            }
          })
          .catch(() => {
            onError(t('posts_bulk_import.errors.parse_file'));
          })
          .finally(() => {
            setIsUploading(false);
          });
      } else {
        setItems([]);
        setFileName('');
      }
    };

    const onUppyComplete = () => {
      uppy.reset();
    };

    const onUppyInfoVisible = () => {
      const { info } = uppy.getState();
      const items = [...info];
      const lastInfo = items.pop();

      if (lastInfo?.message && ['error', 'warning'].includes(lastInfo.type)) {
        showToast({
          description: lastInfo.message,
          title: 'Error',
          status: 'error',
        });
      }
    };

    uppy
      .on('upload', onUppyUpload)
      .on('complete', onUppyComplete)
      .on('info-visible', onUppyInfoVisible);

    return () => {
      uppy
        .off('upload', onUppyUpload)
        .off('complete', onUppyComplete)
        .off('info-visible', onUppyInfoVisible);
    };
  }, [uppy, itemsLimit]);

  useEffect(() => {
    onProgress?.(isUploading || preparingInProgress || importInProgress);
  }, [isUploading, preparingInProgress, importInProgress]);

  useEffect(() => {
    if (importEnd) {
      dispatch(refreshPostsView());
    }
  }, [importEnd]);

  useEffect(() => {
    setItemsLimit(
      getFeatureLimit(subscriptionData, FeatureCodes.PLANNER_BULK_IMPORT) || 100
    );
    setWorkspaceFeatures({
      bulkImport: isFeatureAvailable(
        subscriptionData,
        FeatureCodes.PLANNER_BULK_IMPORT
      ),
      canAddSchedule: isFeatureAvailable(
        subscriptionData,
        FeatureCodes.PLANNER_ADD_SCHEDULE
      ),
      canAddBuffer: isFeatureAvailable(
        subscriptionData,
        FeatureCodes.PLANNER_ADD_TO_QUEUE
      ),
    });
  }, [subscriptionData]);

  useEffect(() => {
    if (
      !automaticTime &&
      !workspaceFeatures.canAddSchedule &&
      workspaceFeatures.canAddBuffer
    ) {
      setAutomaticTime(true);
    } else if (
      automaticTime &&
      workspaceFeatures.canAddSchedule &&
      !workspaceFeatures.canAddBuffer
    ) {
      setAutomaticTime(false);
    }
  }, [workspaceFeatures]);

  useEffect(() => {
    return () => {
      dispatch(setPostsBulkImportItems([]));
    };
  }, []);

  return (
    <>
      {postsBulkImportItems.length ? (
        <Box>
          {!importEnd && !importInProgress && itemsWithValidationError > 0 ? (
            <Alert status="error" showIcon>
              {t('posts_bulk_import.notify.validation_errors')}
            </Alert>
          ) : null}
          {importEnd && !itemsWithImportError && itemsWithImportSuccess > 0 ? (
            <Alert status="success" showIcon>
              {t('posts_bulk_import.notify.import_success')}
            </Alert>
          ) : null}
          {importEnd &&
          itemsWithImportSuccess > 0 &&
          itemsWithImportError > 0 ? (
            <Alert status="error" showIcon>
              {t('posts_bulk_import.notify.import_part_error')}
            </Alert>
          ) : null}
          {importEnd && !itemsWithImportSuccess && itemsWithImportError > 0 ? (
            <Alert status="error" showIcon>
              {t('posts_bulk_import.notify.import_error')}
            </Alert>
          ) : null}
          {itemsWithError.length > 0 ? (
            <Box paddingX="4" paddingY="2" marginTop="2">
              <Flex alignItems="center">
                <Switch
                  id="bi-all-posts"
                  size="sm"
                  isChecked={!!showAllItems}
                  onChange={handleShowAllItemsToggle}
                />
                <Box
                  as="label"
                  htmlFor="bi-all-posts"
                  fontSize="sm"
                  lineHeight="1.2"
                  margin="0"
                  marginLeft="2"
                  cursor="pointer"
                >
                  {t('posts_bulk_import.settings.show_all_posts')}
                </Box>
              </Flex>
            </Box>
          ) : null}
          <Box
            overflowY="auto"
            marginX="4"
            marginY="2"
            padding="2"
            paddingBottom="0"
            maxHeight="60vh"
            borderRadius="md"
            background="blackAlpha.500"
          >
            {itemsToShow.map((item) => (
              <PostsBulkImportItem
                key={item.id}
                accounts={accounts}
                data={item}
                isDisabled={
                  importInProgress ||
                  item.status === ImportPostStatus.IMPORT_SUCCESS
                }
                hasAutomaticTime={automaticTime}
                onDelete={handleDeleteItem}
                onEdit={handleEditItem}
                onClick={handlePreviewItem}
              />
            ))}
          </Box>
          <Flex
            justifyContent="flex-end"
            padding="4"
            borderTop="1px solid"
            borderColor="whiteAlpha.300"
          >
            <ButtonGroup size="sm">
              {importEnd ? (
                <>
                  {itemsWithImportError > 0 ? (
                    <Button onClick={handleRetry}>
                      {t('retry', { ns: 'common' })}
                    </Button>
                  ) : null}
                  <Button variant="solid" onClick={handleClose}>
                    {t('close', { ns: 'common' })}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={handlePreviousStep}
                    isDisabled={importInProgress}
                    leftIcon={<Icon as={FaArrowLeft} />}
                  >
                    {t('back', { ns: 'common' })}
                  </Button>
                  <Button
                    variant="solid"
                    onClick={handleImport}
                    isLoading={importInProgress}
                    loadingText={`${progressPercent}%`}
                  >
                    {t('posts_bulk_import.actions.import')}
                  </Button>
                </>
              )}
            </ButtonGroup>
          </Flex>
        </Box>
      ) : (
        <Box>
          <Box paddingX="4">
            <Flex direction={isSmallScreen ? 'column' : 'row'} paddingY="3">
              <Box flex={isSmallScreen ? null : '1 1 55%'}>
                <Box fontSize="lg" fontWeight="500" marginBottom="2">
                  {t('posts_bulk_import.how_to.title')}
                </Box>
                <Flex alignItems="flex-start" marginBottom="3">
                  <Box width="8">
                    <Icon as={FaCloudDownloadAlt} color="gray.100" />
                  </Box>
                  <Box flex="1">
                    <Box fontSize="sm" fontWeight="500" marginBottom="1">
                      {t('posts_bulk_import.how_to.sample.title')}
                    </Box>
                    <Box fontSize="xs" color="gray.200" marginBottom="2">
                      {t('posts_bulk_import.how_to.sample.description')}
                    </Box>
                    <Button
                      as={Link}
                      isExternal
                      href="https://assets.simplified.co/bulk-import-example.csv"
                      size="xs"
                      leftIcon={<Icon as={FaDownload} />}
                    >
                      {t('posts_bulk_import.how_to.sample.download_file')}
                    </Button>
                  </Box>
                </Flex>
                <Flex alignItems="flex-start" marginBottom="3">
                  <Box width="8">
                    <Icon as={FaPen} color="gray.100" />
                  </Box>
                  <Box flex="1">
                    <Box fontSize="sm" fontWeight="500" marginBottom="1">
                      {t('posts_bulk_import.how_to.details.title')}
                    </Box>
                    <Box fontSize="xs" color="gray.200">
                      <Text marginBottom="1">
                        {t('posts_bulk_import.how_to.details.columns_title')}
                      </Text>
                      <Box paddingX="2">
                        <Text>
                          <b>A</b> -{' '}
                          {t('posts_bulk_import.how_to.details.column_a')}
                        </Text>
                        <Text>
                          <b>B</b> -{' '}
                          {t('posts_bulk_import.how_to.details.column_b')}
                        </Text>
                        <Text>
                          <b>C</b> -{' '}
                          {t('posts_bulk_import.how_to.details.column_c')}
                        </Text>
                        <Text>
                          <b>D</b> -{' '}
                          {t('posts_bulk_import.how_to.details.column_d')}
                        </Text>
                      </Box>
                    </Box>
                  </Box>
                </Flex>
                <Flex alignItems="flex-start">
                  <Box width="8">
                    <Icon as={FaVideo} color="gray.100" />
                  </Box>
                  <Box flex="1">
                    <Box fontSize="sm" fontWeight="500" marginBottom="1">
                      {t('posts_bulk_import.how_to.tutorial.title')}
                    </Box>
                    <Box fontSize="xs" color="gray.200" marginBottom="1">
                      {t('posts_bulk_import.how_to.tutorial.description')}
                    </Box>
                    <Link
                      isExternal
                      href="https://www.youtube.com/watch?v=2-Jk8hXp9kQ"
                      fontSize="sm"
                    >
                      {t('posts_bulk_import.how_to.tutorial.watch_video')}{' '}
                      <Icon as={FaArrowRight} />
                    </Link>
                  </Box>
                </Flex>
              </Box>
              <Flex
                flex={isSmallScreen ? null : '1 1 45%'}
                marginLeft={isSmallScreen ? null : '3'}
                marginTop={isSmallScreen ? '3' : null}
                paddingLeft={isSmallScreen ? null : '3'}
                paddingTop={isSmallScreen ? '3' : null}
                borderLeft={isSmallScreen ? null : '1px solid'}
                borderTop={isSmallScreen ? '1px solid' : null}
                borderColor="whiteAlpha.300"
                direction="column"
              >
                <Box fontSize="lg" fontWeight="500" marginBottom="2">
                  {t('posts_bulk_import.settings.title')}
                </Box>
                <Box fontSize="sm" fontWeight="500" marginBottom="4">
                  {t('posts_bulk_import.settings.account.title')}
                </Box>
                <Box marginBottom="4">
                  {accounts?.length ? (
                    <SocialAccountsDropdown
                      selected={accountId}
                      accounts={accounts}
                      onChange={handleAccountChange}
                      isDisabled={preparingInProgress}
                      width="240px"
                      dropdownBackground="gray.600"
                    />
                  ) : (
                    <Alert status="warning">{t('accounts.no_accounts')}</Alert>
                  )}
                </Box>
                <Box fontSize="sm" fontWeight="500" marginBottom="4">
                  {t('posts_bulk_import.settings.file.title')}
                </Box>
                <Flex alignItems="center" marginBottom="4">
                  <Switch
                    id="bi-automatic-time"
                    size="sm"
                    isChecked={!!automaticTime}
                    onChange={handleAutomaticTimeToggle}
                    isDisabled={
                      !workspaceFeatures.canAddBuffer ||
                      !workspaceFeatures.canAddSchedule
                    }
                  />
                  <Box
                    as="label"
                    htmlFor="bi-automatic-time"
                    fontSize="sm"
                    lineHeight="1.2"
                    margin="0"
                    marginLeft="2"
                    marginRight="1"
                    cursor="pointer"
                  >
                    {t('posts_bulk_import.settings.automatic_publication_time')}
                  </Box>
                  <Tooltip
                    label={t(
                      'posts_bulk_import.settings.automatic_publication_time_details'
                    )}
                    hasArrow
                    placement="top"
                    shouldWrapChildren
                  >
                    <Icon as={FaInfoCircle} fontSize="xs" color="gray.300" />
                  </Tooltip>
                </Flex>

                {items.length && !isUploading ? (
                  <Flex alignItems="center">
                    <Icon as={FaCheck} color="brand.positive-light" />
                    <Box
                      fontSize="sm"
                      color="gray.100"
                      wordBreak="break-all"
                      marginX="2"
                    >
                      {`${fileName || 'file.csv'} (${items.length})`}
                    </Box>
                    <IconButton
                      size="xs"
                      variant="ghost"
                      onClick={handleResetSelectedFile}
                      isDisabled={preparingInProgress}
                      icon={<Icon as={FaTrash} fontSize="sm" />}
                    />
                  </Flex>
                ) : (
                  <UppyDropFile
                    uppy={uppy}
                    note={t('posts_bulk_import.upload.info', {
                      file_size: `${maxFileSize / (1024 * 1024)}MB`,
                      count: itemsLimit,
                    })}
                    isLoading={isUploading}
                  />
                )}
              </Flex>
            </Flex>
          </Box>
          <Flex
            justifyContent="flex-end"
            padding="4"
            borderTop="1px solid"
            borderColor="whiteAlpha.300"
          >
            <ButtonGroup size="sm">
              <Button
                variant="solid"
                onClick={handleNextStep}
                isDisabled={isUploading || !accountId || !items.length}
                isLoading={preparingInProgress}
                loadingText={`${progressPercent}%`}
                rightIcon={<Icon as={FaArrowRight} />}
              >
                {t('next', { ns: 'common' })}
              </Button>
            </ButtonGroup>
          </Flex>
        </Box>
      )}
      {itemPreview ? (
        <PostPreviewModal
          onClose={handleClosePreviewItem}
          accounts={itemPreview?.accounts}
          postsByProvider={itemPreview?.postsByProvider}
        />
      ) : null}
      {itemEdit ? (
        <PostEditorViewModal
          onClose={handleCloseEditItem}
          onActionDone={handleCloseEditItem}
          defaultPostData={itemEdit.post}
          mode={PostEditorModes.CUSTOM_EDIT}
          onCustomAction={handleActionEditItem}
          customHideTimeSelector
        />
      ) : null}
      {showUpgradeModal ? (
        <TldrUpgradeSubscriptionModal
          show={showUpgradeModal}
          onHide={handleCloseUpgradeModal}
        />
      ) : null}
    </>
  );
};

PostsBulkImport.propTypes = {
  accounts: PropTypes.arrayOf(SocialAccountPropType),
  defaultAccountId: PropTypes.string,
  onClose: PropTypes.func,
};

PostsBulkImport.defaultProps = {
  defaultAccountId: undefined,
  onClose: undefined,
};

export default PostsBulkImport;
